<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1 class="title">{{ info.name }} ({{ info.pax }}) {{ info?.lead?.tour_operator ? info.lead.tour_operator.short_name : '' }}</h1>
          <div v-if="info.lead" class="status-title" :style="{ 'backgroundColor': info.lead.status.color || '#8a8a8a' }">
            {{ info.lead.status.name }}
          </div>
          <div
            class="status-title"
            :class="{
            'status-title--in_process': info.lead?.tour_operator?.branding,
            'status-title--checked': !info.lead?.tour_operator?.branding
          }"
          >
            {{ info.lead?.tour_operator?.branding ? 'Partner personal area' : 'Altezza personal area' }}
          </div>
          <div v-if="!is_sales_manager" class="manager-title">
            <div class="manager-title__photo">
              <img :src="getUserAvatar(info.created_by.crop.crop_url)" alt="Avatar">
            </div>
            <span class="manager-title__name">{{ info.created_by.full_name }}</span>
          </div>
        </app-cells>
        <app-cells :indent="false">
          <app-button
            v-if="is_general_manager || is_sales_supervisor || is_support"
            theme="warn"
            size="small"
            @click.native="onShowManagerModal"
          >
            Change manager
          </app-button>
          <a
            v-if="is_superuser"
            :href="`${admin_url}clients/group/${$route.params.id}/change/`"
            target="_blank"
            class="btn btn--blue btn--small"
          >
            Django
          </a>
        </app-cells>
      </app-cells>
      <div v-if="!info.my_group" class="attention-info">
        <img src="@/assets/img/warning-icon.svg" alt="">
        <p>This lead is managed by {{info.created_by.full_name}}. Please be attentive when taking any actions related to it.</p>
      </div>
      <div class="info-cards">
        <div class="info-cards__card">
          <button
            v-if="is_general_manager || is_sales_manager || is_sales_supervisor || is_support"
            class="info-cards__control"
            @click="onShowEditGroupModal"
          >
            <img src="@/assets/img/edit-table-icon.svg" alt="">
          </button>
          <div v-if="is_general_manager || is_sales_manager || is_sales_supervisor || is_support" class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Lead:</span>
              <router-link
                v-if="info.lead"
                :to="{ name: 'leads-detail-lead', params: { id: info.lead.id } }"
                class="info-cards__link"
              >
                {{ info.lead.name }}
              </router-link>
              <span v-else>-</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Story:</span>
              <a
                :href="`https://story.altezza.travel/clients/${info.lead?.final_itinerary?.itinerary_id}`"
                target="_blank"
                class="table-default__activity table-default__activity--itinerary"
                :class="{ 'table-default__activity--active': info.lead?.final_itinerary?.itinerary_id }"
              />
              <a
                :href="`https://story.altezza.travel/itinerary/${info.lead?.final_itinerary?.hash}`"
                target="_blank"
                class="table-default__activity table-default__activity--clients"
                :class="{ 'table-default__activity--active': info.lead?.final_itinerary?.hash }"
              />
            </div>
            <div class="info-cards__row-item">
              <span>External:</span>
              <a
                :href="info.lead?.asana"
                target="_blank"
                class="table-default__activity table-default__activity--asana"
                :class="{'table-default__activity--active': info.lead?.asana}"
              />
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Tour operator:</span>
              <span>{{ info.lead?.tour_operator ? info.lead.tour_operator.name : '-' }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Tour dates:</span>
              <span v-if="info.tour_dates.first_activity && info.tour_dates.last_activity">{{ dateFormat(info.tour_dates.first_activity) }} - {{ dateFormat(info.tour_dates.last_activity) }}</span>
              <span v-else>-</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Travel dates:</span>
              <span v-if="info.transport.arrival && info.transport.departure">{{ dateFormat(info.transport.arrival) }} - {{ dateFormat(info.transport.departure) }}</span>
              <span v-else>-</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Arrival:</span>
              <span
                v-tooltip="{ content: 'Filled / Confirmed / Transfer requested', placement: 'top-start', offset: 10 }"
                class="status-tag"
                :style="{ 'backgroundColor': info.transport.arrival_confirmed === info.pax && info.transport.arrival_transfer_required === info.transport.arrival_transfer_requested ? '#77b017' : info.transport.arrival_is_filled !== info.pax ? '#e85c20' : '#439ad1' }"
              >
                {{ info.transport.arrival_is_filled }} / {{ info.transport.arrival_confirmed }} / {{ info.transport.arrival_transfer_requested }}
              </span>
            </div>
            <div class="info-cards__row-item">
              <span>Departure:</span>
              <span
                v-tooltip="{ content: 'Filled / Confirmed / Transfer requested', placement: 'top-start', offset: 10 }"
                class="status-tag"
                :style="{ 'backgroundColor': info.transport.departure_confirmed === info.pax && info.transport.departure_transfer_required === info.transport.departure_transfer_requested ? '#77b017' : info.transport.departure_is_filled !== info.pax ? '#e85c20' : '#439ad1' }"
              >
                {{ info.transport.departure_is_filled }} / {{ info.transport.departure_confirmed }} / {{ info.transport.departure_transfer_requested }}
              </span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Climbing insurance: </span>
              <span
                class="status-tag"
                :style="{ 'backgroundColor': !info.climbing.climbing_insurance_is_filled ? '#e85c20' : info.climbing.climbing_insurance_is_filled < info.activities.climbing_count ? '#439ad1' : '#77b017' }"
              >
                {{ info.climbing.climbing_insurance_is_filled }}
              </span>
            </div>
            <div class="info-cards__row-item">
              <span>Safari insurance: </span>
              <span
                class="status-tag"
                :style="{ 'backgroundColor': !info.safari.safari_insurance_is_filled ? '#e85c20' : info.safari.safari_insurance_is_filled < info.activities.safari_count ? '#439ad1' : '#77b017' }"
              >
                {{ info.safari.safari_insurance_is_filled }}
              </span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Medical clearance: </span>
              <span
                class="status-tag"
                :style="{ 'backgroundColor': !info.medical_clearance ? '#e85c20' : info.medical_clearance < info.activities.climbing_count ? '#439ad1' : '#77b017' }"
              >
                {{ info.medical_clearance }}
              </span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Rooming list: </span>
              <span
                v-tooltip="{ content: 'Completed / Total', placement: 'top-start', offset: 10 }"
                class="status-tag"
                :style="{ 'backgroundColor': info.reservations.rooming_list_complete === info.reservations.total ? '#77b017' : '#e85c20' }"
              >
                {{ info.reservations.rooming_list_complete }} / {{ info.reservations.total }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <app-cells>
        <app-form-group class="form-group--no-indent" label="Sales manager notes" style="width: 100%">
          <app-textarea
            v-model="info.notes"
            rows="5"
            @change.native="onUpdateNotes"
            placeholder="Write a notes"
          />
        </app-form-group>
      </app-cells>
      <app-tabs :tabs="tabs_to_show" />
      <transition name="fade" mode="out-in">
        <router-view @update-group="getGroupInfo" :groupData="info" />
      </transition>
    </template>
    <modal
      name="primary-client-form"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('primary-client-form')" class="popup__close" type="button"></button>
        <div class="popup__title">Group edit</div>
        <div class="popup__body">
          <form @submit.prevent="sendPrimaryForm">
            <app-form-group label="Name" label-for="group-name">
              <app-input
                v-model="client_primary_form.name"
                id="group-name"
                placeholder="Name"
              />
            </app-form-group>
            <app-form-group label="Primary client">
              <app-select
                v-model="client_primary_form.primary_contact"
                :reduce="item => item.id"
                :options="primary_client_options"
                :clearable="false"
                label="full_name"
                placeholder="Primary client"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button
                :disabled="!client_primary_form.name && !client_primary_form.primary_contact"
                type="submit"
                ref="clientPrimarySubmit"
              >
                Save
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="manager-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('manager-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Transfer clients to another manager</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckManagerForm">
            <app-form-group label="Sales manager" required>
              <app-select
                v-model="manager_form.manager"
                :reduce="item => item.id"
                :options="managers_options"
                label="first_name"
                placeholder="Sales manager"
                :display-field="option => `${option.first_name} ${option.last_name}`"
                :error="$v.manager_form.manager.$error"
              />
              <template #error>
                <div v-if="$v.manager_form.manager.$dirty && !$v.manager_form.manager.required">
                  Required field
                </div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="managerSubmit" :disabled="$v.manager_form.$error">Transfer clients</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import AppTabs from '@/components/AppTabs'
import { required } from 'vuelidate/lib/validators'
import { getClientsGroup, getClientsLite, getUsers, patchClientsGroup, postClientsGroupsChangeManager } from '@/http'
import { format, parse } from 'date-fns'

export default {
  name: 'MyClientsGroupsLayout',
  components: { AppTabs },
  data() {
    return {
      is_loaded: false,
      tabs: [
        { name: 'clients-groups-general', label: 'General', roles: [] },
        { name: 'clients-groups-arrival', label: 'Arrival', roles: [] },
        { name: 'clients-groups-departure', label: 'Departure', roles: [] },
        { name: 'clients-groups-activities', label: 'Activities', roles: [] },
        { name: 'clients-groups-transfers', label: 'Transfers', roles: [] },
        { name: 'clients-groups-reservations', label: 'Reservations', roles: ['general_manager', 'sales_manager', 'operations_manager', 'sales_supervisor', 'operations_supervisor', 'finance_manager'] },
        { name: 'clients-groups-payment-control', label: 'Payment control', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'finance_manager'] },
        { name: 'clients-groups-invoices', label: 'Invoices', roles: ['general_manager', 'operations_supervisor', 'finance_manager'] },
        { name: 'clients-groups-reminders', label: 'Reminders', roles: [] },
        { name: 'clients-groups-cost-breakdown', label: 'Cost Breakdown', roles: ['general_manager'] },
      ],
      tabs_to_show: [],
      client_primary_form: {
        name: '',
        primary_contact: '',
      },
      primary_client_options: [],
      info: {},
      managers_options: [],
      manager_form: {},
      table: {
        items: [],
        columns: [
          'status.name',
          'name',
          'communication',
          'last_contact',
          'activities',
          'travel_time',
          'itinerary',
          'commission',
        ],
        options: {
          headings: {
            'status.name': 'Status',
            'name': 'Name',
            'communication': 'Communication',
            'last_contact': 'Last contact',
            'activities': 'Activities',
            'travel_time': 'Travel time',
            'manager': 'Manager',
            'itinerary': 'Amount',
            'commission': 'Commission',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      status_options: [],
      table_preloader: false,
      admin_url: process.env.VUE_APP_ADMIN_URL,
    }
  },
  validations: {
    manager_form: {
      manager: { required }
    },
  },
  computed: {
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    is_superuser() {
      return this.$store.state.userData.is_superuser
    },
    report_required() {
      if (!this.date_form.methods.length) return false
      return this.date_form.methods.some(item => item.report_required)
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
    is_support() {
      return this.$store.state.userGroups.map(item => item.code).includes('support')
    },
  },
  created() {
    this.getGroupInfo()
    getUsers({ group: 'sales_manager', is_active: true }).then(response => {
      this.managers_options = response.data
    })
  },
  methods: {
    tabsToShow() {
      const tabs = []
      this.tabs.forEach(item => {
        if (!item.roles.length) {
          tabs.push(item)
          return
        }
        item.roles.forEach(role => {
          if (this.$store.state.userGroups.some(groupRole => groupRole.code === role)) tabs.push(item)
        })
      })
      this.tabs_to_show = tabs
    },
    onShowManagerModal() {
      this.manager_form = {}
      this.$v.manager_form.$reset()
      this.$modal.show('manager-modal')
    },
    onCheckManagerForm() {
      this.$v.manager_form.$touch()
      if (this.$v.manager_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendManagerForm()
      }
    },
    sendManagerForm() {
      this.$refs.managerSubmit.preload = true
      postClientsGroupsChangeManager(this.$route.params.id, this.manager_form)
        .then(() => {
          this.getGroupInfo()
          this.$notify({
            type: 'success',
            title: 'Manager changed',
          })
          this.$modal.hide('manager-modal')
        })
        .catch(() => {
          this.$refs.managerSubmit.preload = false
        })
    },
    onShowEditGroupModal() {
      this.client_primary_form.name = this.info.name
      if (this.info.primary_contact) this.client_primary_form.primary_contact = this.info.primary_contact.id
      this.$modal.show('primary-client-form')
    },
    sendPrimaryForm() {
      this.$refs.clientPrimarySubmit.preload = true
      const form = {...this.client_primary_form}
      if (!form.primary_contact) form.primary_contact = null
      patchClientsGroup(this.$route.params.id, form)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Group edited',
          })
          this.getGroupInfo()
          this.$modal.hide('primary-client-form')
          this.client_primary_form = {
            name: '',
            primary_contact: '',
          }
        })
        .catch(() => { this.$refs.clientPrimarySubmit.preload = false })
    },
    onUpdateNotes() {
      patchClientsGroup(this.$route.params.id, { notes: this.info.notes })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Notes saved',
          })
          this.getGroupInfo()
        })
    },
    getGroupInfo() {
      getClientsGroup(this.$route.params.id)
        .then(response => {
          this.info = response.data
          document.title = `${this.info.name} (${this.info.pax}) | Altezza Board`
          this.tabsToShow()
          getClientsLite({ group: this.$route.params.id })
            .then(res => {
              this.primary_client_options = res.data
            })
          this.is_loaded = true
        })
        .catch(() => { this.$router.push({ name: 'clients-groups' }) })
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : ''
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
  }
}
</script>

